import { useQuery } from "@tanstack/react-query"
import { getRouteApi, useLoaderData, useMatch } from "@tanstack/react-router"
import { computed, observable, runInAction } from "mobx"

import { useQueries } from "@/hooks/useRootContext.ts"
import { getLogger } from "@/lib/logging.ts"
import { normalizeProjectKey } from "@/lib/std.ts"

import type { OrganizationPermissions, ProjectKey, ProjectPermissions } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Queries } from "@/lib/queries.ts"
import type { IComputedValue } from "mobx"


export const useCurrentOrgId = () => {
  return getRouteApi("/$orgId").useParams().orgId
}

function useProjectMatch(shouldThrow = false) {
  return useMatch({
    from: "/$orgId/p/$projectKey",
    shouldThrow: shouldThrow,
  })
}

export function useCurrentProject() {
  return useLoaderData({ from: "/$orgId/p/$projectKey" }).project
}

export function useCurrentProjectOrNull() {
  return useProjectMatch()?.loaderData?.project || null
}

export function useProjectPermissions(projectKey: ProjectKey) {
  const client = useQueries().apiClient

  const query = useQuery({
    queryKey: ["getProjectPermissions", normalizeProjectKey(projectKey)],
    queryFn: () => client.getProjectPermissions(projectKey),
    staleTime: Infinity,
  })

  return query.data ?? { canEdit: false, canView: false } as ProjectPermissions
}

export function useOrganizationPermissions() {
  const client = useQueries().apiClient

  const query = useQuery({
    queryKey: ["getOrganizationPermissions"],
    queryFn: () => client.getOrganizationPermissions(),
    staleTime: Infinity,
  })

  return query.data ?? { canInviteUsers: false, canDeleteUsers: false, canChangeRole: false } as OrganizationPermissions
}

const log = getLogger("projectPermissions")

export function orgPermissionsProp(queries: Queries): OrgPermissionsProp {
  const property = observable.box<OrganizationPermissions>({
    canInviteUsers: false,
    canDeleteUsers: false,
    canChangeRole: false,
  }, { deep: false })

  queries.getOrganizationPermissions()
    .then(it => runInAction(() => property.set(it)))
    .catch((e) => log.error("Failed to fetch", e))

  const canInviteUsers = computed(() => property.get().canInviteUsers)
  const canDeleteUsers = computed(() => property.get().canDeleteUsers)
  const canChangeRole = computed(() => property.get().canChangeRole)

  return { canInviteUsers, canDeleteUsers, canChangeRole }
}

export function projectPermissionsProp(queries: Queries, projectKey: ProjectKey): ProjectPermissionsProp {
  const property = observable.box<ProjectPermissions>({ canEdit: false, canView: false }, { deep: false })
  queries.getProjectPermissions(projectKey)
    .then(it => runInAction(() => property.set(it)))
    .catch((e) => log.error("Failed to fetch", e))

  const canEditProject = computed(() => property.get().canEdit)
  const canViewProject = computed(() => property.get().canView)

  return { canEditProject, canViewProject }
}

export type OrgPermissionsProp = {
  readonly canInviteUsers: IComputedValue<boolean>
  readonly canDeleteUsers: IComputedValue<boolean>
  readonly canChangeRole: IComputedValue<boolean>
}

export type ProjectPermissionsProp = {
  readonly canEditProject: IComputedValue<boolean>
  readonly canViewProject: IComputedValue<boolean>
}